import { leadingRow } from "../forecast/multiway.js";
import { visualOf } from "../forecast/visualSpec.js";

// The report's two evidence columns — which arguments go in each, and what they are called.
//
// One derivation shared by the on-screen report and the PDF/DOCX exports, so the three can't
// disagree about the headings or the items. They did: a multiway forecast that carried a
// structured `report` rendered its `reasons_for`/`reasons_against` on screen under the
// BINARY "Evidence for Yes / No" headings, while the exports dropped those reasons entirely
// and printed the older `distribution_reasoning` drivers instead.
//
// On a multi-valued question the server splits the arguments by direction, not by yes/no
// (sooth_agents `_MULTI_ARGUMENT_HEADERS`). On a numeric one they push the value higher /
// lower. On a categorical one `reasons_for` / `reasons_against` argue FOR / AGAINST whichever
// salient outcome each names — not leader vs field — so they are re-sorted here around the
// leading outcome (leaderFieldReasons). The headings and the badge letters follow that.

/**
 * The heading + badge letter for each column, keyed off the question's outcome-space kind.
 *
 * @param {string|null|undefined} kind `null`/`"binary"`/`"boolean"` for a yes/no question,
 *   `"numeric"`, or any other multi-valued kind (categorical)
 * @returns {{left: {label: string, prefix: string}, right: {label: string, prefix: string}}}
 */
export function evidenceColumnLabels(kind) {
  if (!kind || kind === "binary" || kind === "boolean") {
    return {
      left: { label: "Why it could happen", prefix: "Y" },
      right: { label: "Why it might not", prefix: "N" },
    };
  }
  if (kind === "numeric") {
    return {
      left: { label: "Why it could be higher", prefix: "H" },
      right: { label: "Why it could be lower", prefix: "L" },
    };
  }
  return {
    left: { label: "Why this leads", prefix: "L" },
    right: { label: "Why another outcome could win", prefix: "F" },
  };
}

/**
 * A report reason or a distribution driver, normalised to `{claim, detail, strength, citations}`.
 * `detail` is the server's restatement of the claim (at most two sentences, grounded in the
 * cards it cites), when the report carries one.
 */
export function toEvidenceItem(d) {
  return {
    claim: d?.claim || d?.text || "",
    detail: typeof d?.detail === "string" ? d.detail.trim() : "",
    // A chart of the figures the insight and its cited cards state, when the server built one.
    visual: visualOf(d?.visual),
    strength: d?.strength,
    citations: d?.citations || [],
  };
}

const _norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/['"‘’“”]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Which outcome a categorical reason is about, read off the "For <outcome>:" /
 * "Against <outcome>:" prefix the report prompt asks each reason to open with (sooth-agents
 * `_CATEGORICAL_FRAMING`: every reason names the outcome it supports or tells against).
 *
 * The prefix, not a scan of the text: a reason about "'other'" routinely mentions the leader
 * by name ("Temu scored lower than X"), and a text scan would file it under the leader.
 *
 * @param {string} claim
 * @param {string[]} labels the outcome labels, in bucket order
 * @returns {{dir: "for"|"against", index: number}|null} null when there is no prefix or it
 *   names no known outcome
 */
export function reasonOutcome(claim, labels) {
  const m = /^\s*(for|against)\s+(.+?)\s*:/i.exec(String(claim || ""));
  if (!m || !Array.isArray(labels)) return null;
  const dir = m[1].toLowerCase() === "for" ? "for" : "against";
  const named = _norm(m[2]);
  if (!named) return null;
  const norms = labels.map(_norm);
  // Exact first; then the longest label the named outcome starts with, or that starts with
  // it ("X" ↔ "X (formerly Twitter)", "TikTok" ↔ "TikTok / ByteDance").
  let index = norms.indexOf(named);
  if (index < 0) {
    let best = -1;
    let bestLen = 0;
    norms.forEach((l, i) => {
      if (!l) return;
      const hit = named.startsWith(l) || l.startsWith(named);
      if (hit && l.length > bestLen) {
        best = i;
        bestLen = l.length;
      }
    });
    index = best;
  }
  return index >= 0 ? { dir, index } : null;
}

/**
 * Re-sort a CATEGORICAL report's reasons into Evidence for the Leader / Evidence for the Field.
 *
 * Each reason's outcome comes from its optional structured `outcome` field when present (the
 * direction is the list it is in: `reasons_for` argues for that outcome, `reasons_against`
 * against it), else from its claim's "For/Against <outcome>:" prefix.
 *
 * The report produces one flat pair — arguments FOR some salient outcome, arguments AGAINST
 * some salient outcome — so as drawn, both columns were about a mix of different outcomes.
 * Relative to the leading outcome (`leadingRow` — the catch-all only past half the mass):
 *   • for the Leader: FOR the leader, and AGAINST any other outcome (a rival that won't win);
 *   • for the Field:  FOR any other outcome, and AGAINST the leader.
 * A reason whose outcome can't be read stays in the column it came from.
 *
 * @param {object} r the structured report
 * @param {Array<{label: string, p: number}>} buckets the distribution, in any order
 * @returns {{left: object[], right: object[]}|null} null when there is no leader to sort by
 */
export function leaderFieldReasons(r, buckets) {
  if (!Array.isArray(buckets) || buckets.length < 2) return null;
  const labels = buckets.map((b) => b?.label);
  const lead = Math.max(0, buckets.indexOf(leadingRow(buckets)));
  const left = [];
  const right = [];
  const place = (reason, fallbackLeft) => {
    // An OPTIONAL structured `outcome` (a label of the question's own space) is used when a
    // reason carries one — a per-reason field the forecaster's categorical report schema is
    // gaining. Otherwise the claim's "For/Against <outcome>:" prefix, which the current schema
    // asks each reason to open with, and which every existing report relies on.
    const named =
      typeof reason?.outcome === "string" ? labels.map(_norm).indexOf(_norm(reason.outcome)) : -1;
    const o =
      named >= 0
        ? { dir: fallbackLeft ? "for" : "against", index: named }
        : reasonOutcome(reason?.claim ?? reason?.text, labels);
    if (!o) return (fallbackLeft ? left : right).push(reason);
    const aboutLeader = o.index === lead;
    const forLeader = o.dir === "for" ? aboutLeader : !aboutLeader;
    (forLeader ? left : right).push(reason);
  };
  for (const x of r?.reasons_for || []) place(x, true);
  for (const x of r?.reasons_against || []) place(x, false);
  return { left, right };
}

/**
 * The two columns the report shows, or `null` when there is no two-sided evidence.
 *
 * The structured report's reasons win when it has any (every binary report, and a multiway
 * run whose lane authored one); otherwise a multiway run's `distribution_reasoning`
 * drivers — numeric `drivers_higher`/`drivers_lower`, categorical `drivers`/`drivers_against`.
 *
 * @param {object} args
 * @param {object|null} args.r the structured report
 * @param {object|null} args.dr `distribution_reasoning`
 * @param {string|null} args.kind outcome-space kind; `null` for a binary question
 * @param {Array<{label: string, p: number}>|null} [args.buckets] the distribution — on a
 *   categorical question the report's reasons are re-sorted around its leader
 * @returns {{left: {label, prefix, items}, right: {label, prefix, items}, source: "report"|"drivers"}|null}
 */
export function selectEvidenceColumns({ r = null, dr = null, kind = null, buckets = null }) {
  const labels = evidenceColumnLabels(kind);
  const build = (leftRaw, rightRaw, source) => ({
    left: { ...labels.left, items: (leftRaw || []).map(toEvidenceItem) },
    right: { ...labels.right, items: (rightRaw || []).map(toEvidenceItem) },
    source,
  });
  // A report with NO reasons must not shadow the drivers: a partial multiway report can
  // carry an empty `reasons_for`/`reasons_against` while `distribution_reasoning` has them.
  if (r?.reasons_for?.length || r?.reasons_against?.length) {
    const categorical = kind && kind !== "binary" && kind !== "boolean" && kind !== "numeric";
    const sorted = categorical ? leaderFieldReasons(r, buckets) : null;
    if (sorted) return build(sorted.left, sorted.right, "report");
    return build(r.reasons_for, r.reasons_against, "report");
  }
  const multi = kind && kind !== "binary" && kind !== "boolean";
  if (multi && dr) {
    const left = dr.drivers_higher?.length ? dr.drivers_higher : dr.drivers;
    const right = dr.drivers_lower?.length ? dr.drivers_lower : dr.drivers_against;
    if (left?.length || right?.length) return build(left, right, "drivers");
  }
  if (r) return build(r.reasons_for, r.reasons_against, "report");
  return null;
}

const LEADS = "Why this leads";
const ANOTHER_OUTCOME = "Why another outcome could win";
const YES_SIDE = "Why it could happen";
const NO_SIDE = "Why it might not";
const HIGHER = "Why it could be higher";
const LOWER = "Why it could be lower";

/**
 * The insight columns as the brief heads them. A yes/no question always reads Yes first ("Why
 * it could happen", then "Why it might not"), whichever way P(Yes) leans, so every yes/no
 * report reads the same way round. A categorical question leads with the leader ("Why this
 * leads", "Why another outcome could win"); a numeric one keeps higher before lower. Each side
 * keeps its badge letter, so the card chips that link to an insight still find it.
 *
 * @param {ReturnType<typeof selectEvidenceColumns>} columns
 * @param {{kind: string|null, probability?: number|null}} forecast
 */
export function mirabilisInsightColumns(columns, { kind }) {
  if (!columns) return columns;
  const binary = !kind || kind === "binary" || kind === "boolean";
  const [leftLabel, rightLabel] = binary
    ? [YES_SIDE, NO_SIDE]
    : kind === "numeric"
      ? [HIGHER, LOWER]
      : [LEADS, ANOTHER_OUTCOME];
  return {
    ...columns,
    left: { ...columns.left, label: leftLabel },
    right: { ...columns.right, label: rightLabel },
  };
}
